import { useMemo } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

interface MachineData {
  machineId: number;
  machineNumber: string;
  D2: number;
  D5: number;
  RPM: number;
  isOnline: boolean;
  reason: 'on' | 'fault' | 'manual' | 'ignored';
  timestamp: Date;
  statusText: string;
  statusColor: string;
  statusIcon: string;
}

export const useMachineData = (machineId?: number) => {
  const { machineStates, isConnected, connectionStatus } = useWebSocket();

  const machineData = useMemo(() => {
    if (machineId) {
      // Get specific machine data
      const state = machineStates.get(machineId);
      if (!state) return null;

      return {
        ...state,
        statusText: getStatusText(state.reason, state.isOnline),
        statusColor: getStatusColor(state.reason, state.isOnline),
        statusIcon: getStatusIcon(state.reason, state.isOnline)
      } as MachineData;
    } else {
      // Get all machine data
      const allMachines: MachineData[] = [];
      machineStates.forEach((state) => {
        allMachines.push({
          ...state,
          statusText: getStatusText(state.reason, state.isOnline),
          statusColor: getStatusColor(state.reason, state.isOnline),
          statusIcon: getStatusIcon(state.reason, state.isOnline)
        });
      });
      return allMachines;
    }
  }, [machineStates, machineId]);

  const connectionInfo = useMemo(() => {
    return {
      isConnected,
      status: connectionStatus,
      statusText: getConnectionStatusText(connectionStatus),
      statusColor: getConnectionStatusColor(connectionStatus)
    };
  }, [isConnected, connectionStatus]);

  return {
    machineData,
    connectionInfo,
    totalMachines: machineStates.size,
    onlineMachines: Array.from(machineStates.values()).filter(m => m.isOnline).length
  };
};

// Helper functions
function getStatusText(reason: string, isOnline: boolean): string {
  if (isOnline) return 'Running';
  
  switch (reason) {
    case 'fault': return 'Fault';
    case 'manual': return 'Stopped';
    case 'ignored': return 'Offline';
    default: return 'Unknown';
  }
}

function getStatusColor(reason: string, isOnline: boolean): string {
  if (isOnline) return 'text-green-600 bg-green-100';
  
  switch (reason) {
    case 'fault': return 'text-red-600 bg-red-100';
    case 'manual': return 'text-yellow-600 bg-yellow-100';
    case 'ignored': return 'text-gray-600 bg-gray-100';
    default: return 'text-gray-600 bg-gray-100';
  }
}

function getStatusIcon(reason: string, isOnline: boolean): string {
  if (isOnline) return '🟢';
  
  switch (reason) {
    case 'fault': return '🔴';
    case 'manual': return '🟡';
    case 'ignored': return '⚫';
    default: return '❓';
  }
}

function getConnectionStatusText(status: string): string {
  switch (status) {
    case 'connecting': return 'Connecting...';
    case 'connected': return 'Connected';
    case 'disconnected': return 'Disconnected';
    case 'error': return 'Connection Error';
    default: return 'Unknown';
  }
}

function getConnectionStatusColor(status: string): string {
  switch (status) {
    case 'connecting': return 'text-yellow-600 bg-yellow-100';
    case 'connected': return 'text-green-600 bg-green-100';
    case 'disconnected': return 'text-gray-600 bg-gray-100';
    case 'error': return 'text-red-600 bg-red-100';
    default: return 'text-gray-600 bg-gray-100';
  }
}
